import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback. Defaults to a localized "something went wrong" panel. */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Sprint polish: top-level error boundary for the WST ERP frontend.
 *
 * Without it, any uncaught render error (bad data shape, missing
 * translation key, third-party component crash) tears down the
 * whole React tree and leaves the user staring at a blank white
 * page. With it, the user gets a clear "WST ERP учун ҳато юз берди"
 * panel with a "Қайта юклаш" button that re-mounts the app.
 *
 * Designed to be a class component on purpose: error boundaries
 * are the one React feature that has no hook equivalent.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the page usable: the boundary already swallowed the
    // error so React will not unmount, but we still want the
    // details in the console for whoever is debugging.
    // eslint-disable-next-line no-console
    console.error("[wst-erp] Uncaught render error", error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          background: "#f8fafc",
          color: "#0f172a",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: 28,
            boxShadow: "0 10px 25px rgba(15, 23, 42, 0.06)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 10,
              background: "#fef2f2",
              color: "#b91c1c",
              marginBottom: 16,
            }}
          >
            <AlertTriangle size={22} />
          </div>
          <h1 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>
            WST ERP учун ҳато юз берди
          </h1>
          <p style={{ margin: "0 0 16px", color: "#475569", lineHeight: 1.5 }}>
            Саҳифани қайта юклаб кўринг. Агар муаммо такрорланса, қуйидаги хато матнини
            техник ёрдамга юборинг.
          </p>
          <pre
            style={{
              margin: "0 0 20px",
              padding: 12,
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 12,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              color: "#334155",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 160,
              overflow: "auto",
            }}
          >
            {error.name}: {error.message}
          </pre>
          <button
            type="button"
            onClick={this.reset}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 16px",
              background: "#16a34a",
              color: "#fff",
              border: 0,
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            <RotateCcw size={16} /> Қайта юклаш
          </button>
        </div>
      </div>
    );
  }
}
